export const physiologyModule = {
  id: "physiology",
  title: "Renal Physiology: Part 1",
  sections: [
    // ── GFR, Filtration Fraction & Clearance ────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>Glomerular Filtration Rate (GFR) & Clearance</h3>
      <p>The <b class="purple-term">glomerular filtration rate (GFR)</b> is the volume of plasma filtered by the glomeruli per minute. The normal GFR is approximately <strong>125 mL/min</strong>, meaning the kidneys filter about <strong>180 litres</strong> of plasma per day — far more than the ~1.5 L of urine produced. Most of the filtrate is reabsorbed.</p>
      <p>The <b class="purple-term">filtration fraction (FF)</b> is the fraction of renal plasma flow (RPF) that is filtered:
      <br/>FF = GFR / RPF = 125 / 625 = <strong>~20%</strong></p>
      <p>Renal <b class="purple-term">clearance</b> measures how efficiently the kidneys remove a substance from plasma:
      <br/><strong>C = (U × V̇) / P</strong>
      <br/>where U = urine concentration, V̇ = urine flow rate (mL/min), P = plasma concentration.</p>
      <p>If a substance is <em>freely filtered, not reabsorbed, and not secreted</em>, its clearance equals GFR. <b class="purple-term">Inulin</b> satisfies all three conditions and is the gold-standard measure of GFR.</p>
      <p>In clinical practice, <b class="purple-term">creatinine clearance</b> is used to estimate GFR. Creatinine is freely filtered and barely reabsorbed, but is slightly secreted by tubular cells — so creatinine clearance slightly <em>overestimates</em> true GFR. Serum creatinine alone is an unreliable marker of GFR until GFR has fallen by ~50%, as creatinine rises only after significant nephron loss.</p>
      <p><b class="purple-term">Para-aminohippuric acid (PAH)</b> is almost completely secreted in a single pass through the kidney. Its clearance approximates the <strong>effective renal plasma flow (RPF ≈ 625 mL/min)</strong>.</p>
      <div class="highlight-box">Key values to remember:
        <ul>
          <li>GFR: <em>~125 mL/min (180 L/day)</em></li>
          <li>RPF: <em>~625 mL/min</em></li>
          <li>Filtration fraction: <em>~20%</em></li>
          <li>Gold standard for GFR: <em>inulin clearance</em></li>
          <li>Clinical GFR estimate: <em>creatinine clearance (slightly overestimates)</em></li>
        </ul>
      </div>`,
    },
    {
      type: "question",
      text: "Which substance is used as the gold standard for measuring GFR?",
      options: [
        "Creatinine",
        "Para-aminohippuric acid (PAH)",
        "Inulin",
        "Urea",
      ],
      correct: 2,
      explanation:
        "Inulin is the gold standard for GFR measurement because it is freely filtered at the glomerulus, not reabsorbed, and not secreted by the tubules. Therefore its clearance exactly equals the GFR. In clinical practice, creatinine clearance is used as a practical estimate.",
    },
    {
      type: "question",
      text: "Why does creatinine clearance slightly overestimate true GFR?",
      options: [
        "Creatinine is partially reabsorbed in the proximal tubule",
        "Creatinine is partially secreted by tubular cells in addition to being filtered",
        "Creatinine is produced at a variable rate depending on diet",
        "Creatinine binds to plasma proteins and is not freely filtered",
      ],
      correct: 1,
      explanation:
        "Creatinine is freely filtered and minimally reabsorbed, but tubular cells also secrete a small amount of creatinine into the tubular lumen. This means more creatinine appears in the urine than was filtered alone, causing creatinine clearance to slightly overestimate true GFR.",
    },
    {
      type: "question",
      text: "What does PAH clearance measure?",
      options: [
        "GFR",
        "Tubular secretion capacity",
        "Effective renal plasma flow (RPF)",
        "Filtration fraction",
      ],
      correct: 2,
      explanation:
        "PAH (para-aminohippuric acid) is almost completely removed from plasma in a single pass through the kidney (by filtration and secretion). At low plasma concentrations, PAH clearance approximates the effective renal plasma flow (~625 mL/min).",
    },

    // ── Starling Forces & Autoregulation ────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>Starling Forces & Autoregulation of GFR</h3>
      <p>GFR is determined by the <strong>net filtration pressure</strong> across the glomerular capillary wall, governed by Starling forces:</p>
      <ul>
        <li><b class="purple-term">Glomerular capillary hydrostatic pressure (P<sub>GC</sub>)</b> ~55 mmHg → <strong>favours filtration</strong></li>
        <li><b class="purple-term">Bowman's space hydrostatic pressure (P<sub>BS</sub>)</b> ~15 mmHg → <strong>opposes filtration</strong></li>
        <li><b class="purple-term">Glomerular oncotic pressure (π<sub>GC</sub>)</b> ~30 mmHg → <strong>opposes filtration</strong> (plasma proteins retained)</li>
      </ul>
      <p><strong>Net filtration pressure</strong> = P<sub>GC</sub> − P<sub>BS</sub> − π<sub>GC</sub> = 55 − 15 − 30 = <b>10 mmHg</b></p>
      <p>As filtration occurs, plasma proteins become more concentrated in the capillary → oncotic pressure rises → filtration slows (filtration equilibrium).</p>
      <p>Adjusting <strong>arteriolar tone</strong> changes GFR:</p>
      <div class="highlight-box">Arteriolar tone and GFR:
        <ul>
          <li><strong>Afferent dilation</strong> (prostaglandins, ANP) → ↑P<sub>GC</sub> → ↑GFR</li>
          <li><strong>Afferent constriction</strong> (NSAIDs, severe hypotension) → ↓P<sub>GC</sub> → ↓GFR</li>
          <li><strong>Efferent constriction</strong> (angiotensin II) → ↑P<sub>GC</sub> → ↑GFR (short term)</li>
          <li><strong>Efferent dilation</strong> (ACE inhibitors, ARBs) → ↓P<sub>GC</sub> → ↓GFR — reduces proteinuria</li>
        </ul>
      </div>
      <p><b class="purple-term">Autoregulation</b> keeps GFR relatively constant over a wide range of mean arterial pressures (MAP 70–180 mmHg) via two mechanisms:</p>
      <p>1. <b class="purple-term">Myogenic reflex</b>: when MAP rises, the afferent arteriole wall is stretched → smooth muscle contracts → afferent constriction → P<sub>GC</sub> stays constant.</p>
      <p>2. <b class="purple-term">Tubuloglomerular feedback (TGF)</b>: when GFR rises, more NaCl reaches the macula densa → macula densa signals afferent arteriole to constrict → GFR falls back to normal. This was covered in the histology module (JGA).</p>`,
    },
    {
      type: "question",
      text: "Why are NSAIDs dangerous in patients with chronic kidney disease or hypovolaemia?",
      options: [
        "NSAIDs block angiotensin II, dilating the efferent arteriole and dropping GFR",
        "NSAIDs inhibit prostaglandins, which normally dilate the afferent arteriole — removing this causes afferent constriction and a sharp drop in GFR",
        "NSAIDs directly damage tubular cells, causing acute tubular necrosis",
        "NSAIDs increase aldosterone, causing sodium retention and oedema",
      ],
      correct: 1,
      explanation:
        "In states of reduced renal perfusion (CKD, hypovolaemia, heart failure), prostaglandins dilate the afferent arteriole to maintain GFR. NSAIDs inhibit prostaglandin synthesis, removing this compensatory dilation. The afferent arteriole constricts, P(GC) drops, and GFR can fall sharply — precipitating AKI.",
    },
    {
      type: "question",
      text: "How do ACE inhibitors reduce proteinuria in diabetic nephropathy?",
      options: [
        "They reduce glomerular oncotic pressure by lowering plasma proteins",
        "They block angiotensin II, dilating the efferent arteriole and reducing glomerular capillary pressure",
        "They increase prostaglandin synthesis, dilating the afferent arteriole",
        "They directly block the filtration slits in the glomerular barrier",
      ],
      correct: 1,
      explanation:
        "Angiotensin II preferentially constricts the efferent arteriole, raising glomerular capillary pressure (P(GC)) and driving protein across the damaged filtration barrier. ACE inhibitors block angiotensin II production, dilating the efferent arteriole, reducing P(GC), and decreasing proteinuria — slowing progression of diabetic nephropathy.",
    },
    {
      type: "question",
      text: "What is the net filtration pressure across the glomerular capillary wall?",
      options: [
        "~40 mmHg",
        "~30 mmHg",
        "~10 mmHg",
        "~55 mmHg",
      ],
      correct: 2,
      explanation:
        "Net filtration pressure = P(GC) − P(BS) − π(GC) = 55 − 15 − 30 = 10 mmHg. Although the driving pressure seems low, the high hydraulic conductivity of the glomerular capillary wall and the large surface area allow the kidneys to filter ~180 L per day.",
    },

    // ── PCT Reabsorption ─────────────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>Proximal Convoluted Tubule (PCT) Reabsorption</h3>
      <p>The PCT reabsorbs approximately <strong>65%</strong> of the filtered Na⁺, water, HCO₃⁻, glucose, amino acids, and phosphate. The driving force is the <b class="purple-term">Na⁺/K⁺-ATPase</b> on the basolateral membrane, which keeps intracellular Na⁺ low, creating a gradient for Na⁺ to enter from the lumen.</p>
      <p><strong>Glucose reabsorption:</strong> Glucose is cotransported with Na⁺ across the apical membrane by <b class="purple-term">SGLT2</b> (sodium-glucose cotransporter 2), then exits the cell basolaterally via <b class="purple-term">GLUT2</b>. SGLT2 has a <b class="purple-term">transport maximum (Tm)</b> of ~375 mg/min. When plasma glucose exceeds ~200 mg/dL, the filtered load exceeds Tm and glucose spills into the urine (<b class="purple-term">glycosuria</b>).</p>
      <p><strong>HCO₃⁻ reabsorption:</strong> H⁺ is secreted into the lumen via the NHE3 exchanger. Luminal H⁺ combines with filtered HCO₃⁻ → H₂CO₃ → CO₂ + H₂O (catalysed by luminal <b class="purple-term">carbonic anhydrase IV</b>). CO₂ diffuses into the cell, where intracellular carbonic anhydrase II regenerates H⁺ and HCO₃⁻. HCO₃⁻ then exits basolaterally.</p>
      <p><strong>Organic acid secretion:</strong> Many drugs and toxins (e.g. PAH, penicillin, uric acid) are secreted into the tubule by organic anion transporters (OAT) — this is how probenecid blocks uric acid secretion in gout.</p>
      <div class="mnemonic-card">
        <div class="mnemonic-title">PCT reabsorbs: "GLUCOSE CAMP"</div>
        <div class="mnemonic-content">
          <ul>
            <li><b class="purple-term">G</b>lucose (SGLT2 apical, GLUT2 basolateral)</li>
            <li><b class="purple-term">L</b>actate and other organic anions</li>
            <li><b class="purple-term">U</b>rea (50% reabsorbed passively)</li>
            <li><b class="purple-term">C</b>O₂/HCO₃⁻ (carbonic anhydrase-dependent)</li>
            <li><b class="purple-term">O</b>smotic water (follows Na⁺ — isosmotic reabsorption)</li>
            <li><b class="purple-term">S</b>odium (65%, via NHE3 and other cotransporters)</li>
            <li><b class="purple-term">E</b>lectrolytes: K⁺, Cl⁻, Ca²⁺, Mg²⁺, phosphate</li>
            <li><b class="purple-term">A</b>mino acids</li>
            <li><b class="purple-term">M</b>ost of the filtered load of the above</li>
            <li><b class="purple-term">P</b>hosphate (regulated by PTH)</li>
          </ul>
        </div>
      </div>`,
    },
    {
      type: "question",
      text: "A type 2 diabetic patient with poorly controlled blood glucose (plasma glucose 350 mg/dL) develops glucose in their urine. What explains this?",
      options: [
        "The glomerular filtration barrier breaks down at high plasma glucose levels",
        "High plasma glucose exceeds the transport maximum (Tm) of SGLT2, so glucose cannot be fully reabsorbed",
        "High plasma glucose inhibits GLUT2 on the basolateral membrane",
        "Glucose is secreted into the tubule at high plasma concentrations",
      ],
      correct: 1,
      explanation:
        "SGLT2 in the PCT reabsorbs glucose by cotransport with Na⁺. Its transport maximum (Tm) is ~375 mg/min (threshold plasma glucose ~200 mg/dL). When plasma glucose is very high, the filtered glucose load exceeds the Tm of SGLT2 and the excess glucose cannot be reabsorbed, appearing in the urine (glycosuria).",
    },
    {
      type: "question",
      text: "SGLT2 inhibitors (e.g. empagliflozin) are used in type 2 diabetes. What is their mechanism?",
      options: [
        "They block aldosterone receptors, reducing sodium retention",
        "They inhibit SGLT2 in the PCT, preventing glucose reabsorption and causing glucosuria",
        "They increase GFR by dilating the afferent arteriole",
        "They block the NKCC2 cotransporter in the thick ascending limb",
      ],
      correct: 1,
      explanation:
        "SGLT2 inhibitors block the SGLT2 cotransporter in the PCT, preventing glucose reabsorption. This causes glucosuria, lowering blood glucose. They also cause a mild osmotic diuresis, reduce blood pressure, and have proven cardioprotective and renoprotective effects independent of glucose lowering.",
    },
    {
      type: "question",
      text: "Which enzyme in the PCT lumen is essential for bicarbonate reabsorption?",
      options: [
        "Lactate dehydrogenase",
        "Carbonic anhydrase IV",
        "Alkaline phosphatase",
        "Phospholipase A2",
      ],
      correct: 1,
      explanation:
        "Luminal carbonic anhydrase IV (CA IV) catalyses the conversion of H⁺ + HCO₃⁻ → CO₂ + H₂O in the tubular lumen. CO₂ then diffuses into the cell, where intracellular CA II regenerates HCO₃⁻ for basolateral exit. Acetazolamide (a carbonic anhydrase inhibitor) blocks this process, causing bicarbonate loss and metabolic acidosis.",
    },
    {
      type: "question",
      text: "The PCT reabsorbs approximately what percentage of the filtered sodium and water?",
      options: ["25%", "45%", "65%", "85%"],
      correct: 2,
      explanation:
        "The PCT reabsorbs ~65% of the filtered Na⁺, water, HCO₃⁻, glucose, and amino acids. This is the bulk reabsorption phase. The remaining 35% is then fine-tuned by the loop of Henle, DCT, and collecting duct.",
    },

    // ── Loop of Henle & Countercurrent Multiplication ───────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>Loop of Henle & Countercurrent Multiplication</h3>
      <p>The loop of Henle's primary role is to build the <strong>medullary osmolality gradient</strong> that allows the kidney to produce concentrated urine. This is achieved by <b class="purple-term">countercurrent multiplication</b>.</p>
      <p><strong>Thin descending limb:</strong> Permeable to water but not to Na⁺/Cl⁻. As it descends into the hypertonic medulla, water is drawn out osmotically → tubular fluid becomes <strong>progressively more concentrated</strong> (up to ~1200 mOsm at the tip of the loop).</p>
      <p><strong>Thin ascending limb:</strong> Permeable to Na⁺ and Cl⁻ but not water. Solutes passively diffuse out into the medullary interstitium.</p>
      <p><strong>Thick ascending limb (TAL):</strong> The <b class="purple-term">NKCC2 cotransporter</b> actively pumps Na⁺, K⁺, and 2Cl⁻ out of the lumen. This segment is <strong>completely impermeable to water</strong>, so solute is removed without water — the tubular fluid <em>dilutes</em> as it ascends, while the medullary interstitium becomes more concentrated.</p>
      <p>The result is a <strong>corticopapillary osmolality gradient</strong>: ~300 mOsm at the cortex → ~1200 mOsm at the papilla. This gradient is the driving force for water reabsorption in the collecting duct when ADH is present.</p>
      <p>The <b class="purple-term">vasa recta</b> preserve this gradient by <b class="purple-term">countercurrent exchange</b>: blood descends and takes up solute (concentrating), then ascends and loses solute back (diluting), with slow flow preventing washout of the gradient.</p>
      <div class="highlight-box">Countercurrent multiplication summary:
        <ul>
          <li>Descending limb: <em>water leaves → tubular fluid concentrates</em></li>
          <li>Thick ascending limb: <em>Na⁺/K⁺/Cl⁻ pumped out (NKCC2), impermeable to water → tubular fluid dilutes</em></li>
          <li>Net result: <em>medullary gradient 300–1200 mOsm</em></li>
          <li>Vasa recta: <em>countercurrent exchange preserves the gradient</em></li>
        </ul>
      </div>`,
    },
    {
      type: "question",
      text: "Why is the thick ascending limb of Henle critical for producing concentrated urine?",
      options: [
        "It secretes ADH directly into the tubular fluid",
        "It pumps Na⁺/K⁺/Cl⁻ out of the lumen while being impermeable to water, building the medullary concentration gradient",
        "It reabsorbs urea, which contributes to the medullary gradient",
        "It secretes H⁺ to acidify the tubular fluid",
      ],
      correct: 1,
      explanation:
        "The thick ascending limb pumps Na⁺, K⁺, and Cl⁻ out of the tubular lumen via NKCC2, but is completely impermeable to water. This dilutes the tubular fluid while concentrating the medullary interstitium — building the osmolality gradient from ~300 to ~1200 mOsm. Without this gradient, ADH cannot concentrate urine.",
    },
    {
      type: "question",
      text: "A patient on furosemide passes large volumes of dilute urine. What is the mechanism?",
      options: [
        "Furosemide blocks aquaporin-2 channels in the collecting duct",
        "Furosemide blocks NKCC2 in the thick ascending limb, destroying the medullary gradient and preventing urine concentration",
        "Furosemide inhibits aldosterone receptors in the collecting duct",
        "Furosemide blocks Na⁺/Cl⁻ cotransport in the DCT",
      ],
      correct: 1,
      explanation:
        "Furosemide (a loop diuretic) blocks the NKCC2 cotransporter in the thick ascending limb. Without NKCC2, the kidney cannot build the medullary osmolality gradient. Even if ADH is present, there is no gradient to drive water reabsorption in the collecting duct — resulting in large volumes of dilute urine.",
    },
    {
      type: "question",
      text: "What is the key functional difference between the descending and thick ascending limbs of Henle?",
      options: [
        "The descending limb is impermeable to water; the ascending limb is permeable to water",
        "The descending limb is permeable to water but not Na⁺; the thick ascending limb is impermeable to water but actively pumps Na⁺/K⁺/Cl⁻ out",
        "Both limbs are equally permeable to water and sodium",
        "The descending limb pumps Na⁺ in; the ascending limb pumps Na⁺ out",
      ],
      correct: 1,
      explanation:
        "The thin descending limb is permeable to water (not Na⁺) → water leaves, tubular fluid concentrates. The thick ascending limb actively pumps Na⁺/K⁺/Cl⁻ out via NKCC2, but is completely impermeable to water → tubular fluid dilutes. This differential permeability is what drives countercurrent multiplication.",
    },

    // ── DCT & Collecting Duct ───────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>Distal Convoluted Tubule (DCT) & Collecting Duct</h3>
      <p>The DCT and collecting duct are the <strong>fine-tuning</strong> segments, responsible for the final adjustments to Na⁺, K⁺, Ca²⁺, H⁺, and water under hormonal control.</p>
      <p><strong>DCT:</strong> Reabsorbs Na⁺ and Cl⁻ via the <b class="purple-term">NCC</b> (Na-Cl cotransporter), the target of <strong>thiazide diuretics</strong>. PTH acts here to increase Ca²⁺ reabsorption via TRPV5 channels and decrease phosphate reabsorption.</p>
      <p><strong>Collecting duct — principal cells:</strong></p>
      <ul>
        <li><b class="purple-term">ADH (vasopressin)</b> binds V2 receptors → cAMP → inserts <b class="purple-term">aquaporin-2 (AQP2)</b> channels into the apical membrane → water is reabsorbed down the medullary gradient → concentrated urine</li>
        <li><b class="purple-term">Aldosterone</b> binds mineralocorticoid receptors → increases <b class="purple-term">ENaC</b> (Na⁺ channels) and <b class="purple-term">ROMK</b> (K⁺ channels) expression → Na⁺ reabsorption and K⁺ secretion → blood pressure ↑, plasma K⁺ ↓</li>
      </ul>
      <p><strong>Collecting duct — intercalated cells:</strong></p>
      <ul>
        <li><strong>Type A intercalated cells</strong>: secrete H⁺ (H⁺-ATPase on apical membrane) and reabsorb HCO₃⁻ — activated in <strong>metabolic acidosis</strong></li>
        <li><strong>Type B intercalated cells</strong>: secrete HCO₃⁻ and reabsorb H⁺ — activated in <strong>metabolic alkalosis</strong></li>
      </ul>
      <div class="mnemonic-card">
        <div class="mnemonic-title">Collecting duct hormones</div>
        <div class="mnemonic-content">
          <ul>
            <li><b class="purple-term">ADH</b> → AQP2 insertion → water reabsorption → concentrated urine</li>
            <li><b class="purple-term">Aldosterone</b> → ↑ENaC + ROMK → Na⁺ in, K⁺ out</li>
            <li><b class="purple-term">No ADH</b> → dilute urine (diabetes insipidus)</li>
            <li><b class="purple-term">No aldosterone</b> → Na⁺ lost, K⁺ retained (Addison's disease)</li>
          </ul>
        </div>
      </div>`,
    },
    {
      type: "question",
      text: "What happens to urine production when ADH is absent (e.g. in central diabetes insipidus)?",
      options: [
        "The kidneys produce small volumes of concentrated urine",
        "The kidneys produce large volumes of dilute urine because aquaporin-2 is not inserted into principal cells",
        "GFR increases, producing more urine of normal concentration",
        "The collecting duct becomes permeable to Na⁺, increasing diuresis",
      ],
      correct: 1,
      explanation:
        "Without ADH, aquaporin-2 channels are not inserted into principal cell apical membranes. The collecting duct remains impermeable to water, even though a medullary osmolality gradient exists. The result is large volumes of very dilute urine (up to 20 L/day in severe DI), causing hypernatremia and dehydration.",
    },
    {
      type: "question",
      text: "How does aldosterone cause hypertension when chronically elevated?",
      options: [
        "It constricts the afferent arteriole, raising glomerular pressure",
        "It increases ENaC expression in principal cells, causing Na⁺ retention and water retention, raising blood volume and pressure",
        "It inhibits ANP, reducing natriuresis",
        "It increases ADH secretion, causing water retention",
      ],
      correct: 1,
      explanation:
        "Aldosterone increases ENaC (Na⁺ channel) and ROMK (K⁺ channel) expression in collecting duct principal cells. More Na⁺ is reabsorbed → water follows osmotically → blood volume increases → blood pressure rises. Chronic hyperaldosteronism (Conn's syndrome) presents with hypertension and hypokalemia.",
    },
    {
      type: "question",
      text: "Which cells in the collecting duct regulate acid-base balance by secreting H⁺?",
      options: [
        "Principal cells via aquaporin-2",
        "Type A intercalated cells via H⁺-ATPase",
        "Type B intercalated cells via Cl⁻/HCO₃⁻ exchanger",
        "Mesangial cells via carbonic anhydrase",
      ],
      correct: 1,
      explanation:
        "Type A intercalated cells secrete H⁺ into the tubular lumen via H⁺-ATPase and H⁺/K⁺-ATPase, while reabsorbing HCO₃⁻ basolaterally. They are activated in metabolic acidosis. Type B intercalated cells do the opposite — they secrete HCO₃⁻ and are activated in metabolic alkalosis.",
    },

    // ── Diuretics ───────────────────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>Diuretics</h3>
      <p>Diuretics increase urine output by blocking specific transporters along the nephron. Each class acts at a different segment:</p>
      <p><b class="purple-term">Carbonic anhydrase inhibitors (acetazolamide)</b> — <strong>PCT</strong>
      <br/>Block CA → can't regenerate H⁺ → ↓NHE3 activity → HCO₃⁻ not reabsorbed → bicarbonaturia and NaHCO₃ loss → <strong>metabolic acidosis</strong>.
      <br/>Uses: glaucoma (↓aqueous humor production), altitude sickness, alkalinise urine.</p>
      <p><b class="purple-term">Loop diuretics (furosemide, bumetanide)</b> — <strong>thick ascending limb</strong>
      <br/>Block NKCC2 → most potent diuretics (can increase fractional Na⁺ excretion to ~25%).
      <br/>Lose: Na⁺, K⁺, Cl⁻, Mg²⁺, <strong>Ca²⁺</strong> (hypocalcaemia).
      <br/>Uses: acute pulmonary oedema, heart failure, hypercalcaemia, refractory oedema.
      <br/>Side effects: hypokalaemia, hypomagnesaemia, metabolic alkalosis, ototoxicity (especially ethacrynic acid), hyperuricaemia.</p>
      <p><b class="purple-term">Thiazide diuretics (hydrochlorothiazide, chlorthalidone)</b> — <strong>early DCT</strong>
      <br/>Block NCC → lose Na⁺, Cl⁻, K⁺, Mg²⁺, but <strong>retain Ca²⁺</strong> (hypercalcaemia — unlike loop diuretics).
      <br/>Uses: hypertension, heart failure, hypercalciuria (kidney stones), osteoporosis.
      <br/>Side effects: hypokalaemia, hyponatraemia, metabolic alkalosis, hyperglycaemia, hyperlipidaemia, hyperuricaemia.</p>
      <p><b class="purple-term">Potassium-sparing diuretics</b> — <strong>collecting duct</strong>
      <br/>Spironolactone/eplerenone: block aldosterone receptors → ↓ENaC and ROMK → Na⁺ lost, K⁺ retained.
      <br/>Amiloride/triamterene: directly block ENaC.
      <br/>Side effects: <strong>hyperkalaemia</strong> (dangerous with ACE inhibitors), gynaecomastia (spironolactone).
      <br/>Uses: Conn's syndrome, heart failure (spironolactone), ascites, hypokalaemia correction alongside other diuretics.</p>
      <p><b class="purple-term">Osmotic diuretics (mannitol)</b> — PCT and loop
      <br/>Filtered but not reabsorbed → osmotic drag retains water in the lumen.
      <br/>Uses: cerebral oedema (↓ICP), AKI prevention (contrast nephropathy, rhabdomyolysis).
      <br/>Caution: initial plasma volume expansion can precipitate pulmonary oedema in heart failure.</p>
      <div class="highlight-box">Which diuretic affects calcium?
        <ul>
          <li>Loop diuretics → ↓Ca²⁺ (used in hypercalcaemia)</li>
          <li>Thiazide diuretics → ↑Ca²⁺ (used in hypercalciuria/kidney stones)</li>
          <li>Mnemonic: <em>"Loops Lose calcium, Thiazides Take calcium"</em></li>
        </ul>
      </div>`,
    },
    {
      type: "question",
      text: "A patient with heart failure is started on furosemide. What electrolyte disturbances should you monitor for?",
      options: [
        "Hyperkalaemia and hypernatraemia",
        "Hypocalcaemia and hypermagnesaemia",
        "Hypokalaemia, hypomagnesaemia, and hyponatraemia",
        "Hypercalcaemia and hypokalaemia",
      ],
      correct: 2,
      explanation:
        "Loop diuretics block NKCC2, increasing urinary excretion of Na⁺, K⁺, Cl⁻, Mg²⁺, and Ca²⁺. The main electrolyte concerns are hypokalaemia (predisposes to arrhythmias, especially with digoxin), hypomagnesaemia (also arrhythmogenic), and hyponatraemia. Metabolic alkalosis can also develop.",
    },
    {
      type: "question",
      text: "A patient with recurrent calcium oxalate kidney stones is found to have hypercalciuria. Which diuretic class would be most appropriate?",
      options: [
        "Loop diuretics (furosemide)",
        "Thiazide diuretics (hydrochlorothiazide)",
        "Potassium-sparing diuretics (amiloride)",
        "Carbonic anhydrase inhibitors (acetazolamide)",
      ],
      correct: 1,
      explanation:
        "Thiazide diuretics block NCC in the early DCT and paradoxically increase Ca²⁺ reabsorption (via TRPV5 channels). This reduces urinary calcium excretion (hypocalciuria), decreasing calcium stone formation. Loop diuretics have the opposite effect — they increase Ca²⁺ excretion and would worsen hypercalciuria.",
    },
    {
      type: "question",
      text: "Why must potassium-sparing diuretics be used with caution alongside ACE inhibitors?",
      options: [
        "Both drugs reduce GFR and can cause AKI",
        "ACE inhibitors increase aldosterone; adding an aldosterone blocker causes sodium overload",
        "Both drugs raise serum potassium via different mechanisms, creating a risk of life-threatening hyperkalaemia",
        "ACE inhibitors block carbonic anhydrase, interfering with bicarbonate handling",
      ],
      correct: 2,
      explanation:
        "ACE inhibitors reduce angiotensin II, which reduces aldosterone secretion — this already tends to raise serum K⁺. Adding a potassium-sparing diuretic (aldosterone antagonist or ENaC blocker) further impairs K⁺ secretion. The combination can cause severe, potentially fatal hyperkalaemia, particularly in patients with CKD.",
    },
    {
      type: "question",
      text: "Which diuretic class is used to treat acute pulmonary oedema and hypercalcaemia?",
      options: [
        "Thiazide diuretics",
        "Potassium-sparing diuretics",
        "Carbonic anhydrase inhibitors",
        "Loop diuretics",
      ],
      correct: 3,
      explanation:
        "Loop diuretics (e.g. furosemide IV) are the treatment of choice for acute pulmonary oedema — their rapid and powerful diuresis reduces preload and relieves congestion. They also increase urinary Ca²⁺ excretion (by blocking NKCC2 in the thick ascending limb), making them useful in hypercalcaemia.",
    },
  ],
};
